import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Plus, Trash2, Users } from 'lucide-react';
import type { ScopeType } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { invalidatePermissions, useCan } from '@/lib/permissions';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Avatar, Badge, Button, Select, Spinner, ask } from '@/components/ui';
import { SCOPE_ORDER, ScopePicker, roleKeys, scopeLabel, useRoles } from './common';
import { PeoplePicker } from '@/features/people/PeoplePicker';
import { AudiencePicker } from '@/features/people/AudiencePicker';

type Props =
  | { mode: 'principal'; principalType: 'user' | 'team'; principalId: string; title?: string; compact?: boolean }
  | { mode: 'scope'; scope: string; title?: string; compact?: boolean };

/**
 * Role assignments for one person/team (mode=principal: "what roles does Sarah hold, where?") or for one
 * resource (mode=scope: "who has extra roles at the Wrexham Store?"). Grant/revoke need admin.roles.manage;
 * the server enforces every rule (Owner only by Owners, membership roles are not grantable, ...).
 */
export function GrantsPanel(props: Props) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const canManage = useCan('admin.roles.manage');
  const canCheck = useCan('admin.permissions.check');
  const roles = useRoles().data ?? [];
  const filter = props.mode === 'principal' ? (props.principalType === 'user' ? { user_id: props.principalId } : { team_id: props.principalId }) : { scope: props.scope };
  const grants = useQuery({ queryKey: roleKeys.grants(ws ?? '', filter), queryFn: () => api.roleGrants(ws!, filter), enabled: Boolean(ws && (canManage || canCheck)) });
  // For people: also show what they inherit from teams and memberships (read-only).
  const inherited = useQuery({
    queryKey: ['perm-check', ws, props.mode === 'principal' ? props.principalId : '', 'workspace'],
    queryFn: () => api.checkPermissions(ws!, (props as { principalId: string }).principalId),
    enabled: Boolean(ws && canCheck && props.mode === 'principal' && props.principalType === 'user'),
  });
  const grantable = roles.filter(r => r.kind === 'workspace');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ role_id: '', scope: props.mode === 'scope' ? props.scope : 'workspace', principal: '' });
  const scopeType = (props.mode === 'scope' ? props.scope.split(':')[0] : 'workspace') as ScopeType;
  // t655u: a role for a team goes to people only (never the team itself or another team), with Save/Cancel.
  const peopleOnly = props.mode === 'scope' && scopeType === 'team';

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['role-grants'] });
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
    void queryClient.invalidateQueries({ queryKey: ['perm-check'] });
    void invalidatePermissions();
  };
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast(ok, 'success'); refresh(); return true; } catch (e) { toast(errorMessage(e), 'error'); return false; }
  };
  const add = async () => {
    const role_id = form.role_id || grantable.find(r => r.system_key === 'supervisor')?.id || grantable[0]?.id;
    if (!role_id) return;
    let principal_type: 'user' | 'team'; let principal_id: string; let scope: string;
    if (props.mode === 'principal') { principal_type = props.principalType; principal_id = props.principalId; scope = form.scope; }
    else { const [t, id] = form.principal.split(':'); principal_type = t as 'user' | 'team'; principal_id = id; scope = props.scope; }
    if (!principal_id) return toast(peopleOnly ? 'Choose a person' : 'Choose a person, department or group', 'error');
    if (await run(() => api.grantRole(ws!, { role_id, principal_type, principal_id, scope }), 'Role granted')) setAdding(false);
  };

  const indirect = useMemo(() => (inherited.data?.grants ?? []).filter(g => g.via !== 'user'), [inherited.data]);
  if (!canManage && !canCheck) return null;

  return (
    <section className="space-y-2" aria-label="Roles and access">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold">{props.title ?? 'Roles and access'}</h3>
        {canManage && !adding && <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{peopleOnly ? 'Add manager' : 'Grant role'}</Button>}
      </div>
      {props.mode === 'scope' && !props.compact && (
        <p className="text-xs text-slate-500">Roles granted here apply only to this {scopeType === 'location' ? 'building and everything placed in it' : scopeType === 'team' ? "department or group and its members' records" : scopeType === 'project' ? 'project' : 'channel'}. Workspace-wide roles are managed under Admin → Roles.</p>
      )}
      {adding && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-accent-line/25 bg-accent-soft/50 p-2 dark:border-slate-700 dark:bg-slate-900">
          <Select aria-label="Role" className="h-8 w-60 py-1" value={form.role_id || grantable.find(r => r.system_key === 'supervisor')?.id || ''} onChange={e => setForm({ ...form, role_id: e.target.value })}>
            {grantable.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
          {props.mode === 'principal'
            ? <ScopePicker className="min-w-[22rem] flex-1" value={form.scope} onChange={scope => setForm({ ...form, scope })}
                allowed={props.principalType === 'team' ? SCOPE_ORDER.filter(x => x !== 'team') : undefined} />
            : (
              // t683u: the shared pickers (name search, no suggested people). A department's or group's managers are people.
              peopleOnly ? (
                <div className="w-64" data-testid="grant-person">
                  <PeoplePicker single label="Person" placeholder="Search for a person" selected={form.principal.startsWith('user:') ? [form.principal.slice(5)] : []}
                    onChange={ids => setForm({ ...form, principal: ids[0] ? `user:${ids[0]}` : '' })} />
                </div>
              ) : (
                <div className="w-72" data-testid="grant-principal">
                  <AudiencePicker label="Person, department or group" placeholder="Search people, departments and groups"
                    value={{ people: form.principal.startsWith('user:') ? [form.principal.slice(5)] : [], teams: form.principal.startsWith('team:') ? [form.principal.slice(5)] : [] }}
                    onChange={v => {
                      const cur = form.principal.slice(5);
                      const p = v.people.find(id => id !== cur); const t = v.teams.find(id => id !== cur);
                      setForm({ ...form, principal: p ? `user:${p}` : t ? `team:${t}` : '' });
                    }}
                    helper="Everyone in a department or group inherits the role." />
                </div>
              )
            )}
          <Button size="sm" onClick={() => void add()}>{peopleOnly ? 'Save' : 'Grant'}</Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}
      {grants.isLoading ? <Spinner /> : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
          {(grants.data ?? []).map(g => (
            <li key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              {props.mode === 'scope' && (g.principal_type === 'user' ? <Avatar size={22} name={g.principal_name} id={g.principal_id} /> : <Users className="h-4 w-4 text-slate-500" />)}
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  {props.mode === 'scope' && <span className="font-medium">{g.principal_name}{g.principal_type === 'team' && ' (team)'} · </span>}
                  <span className="font-medium">{g.role_name}</span>
                  {props.mode === 'principal' && <span className="text-slate-500"> · {scopeLabel(g.scope, g.scope_name)}</span>}
                </div>
                {!props.compact && <div className="text-xs text-slate-500">{g.source === 'default' ? 'Default role for new members' : g.source === 'workforce_role' ? 'Set from the employee record' : `Granted by ${g.created_by_name ?? 'migration'}`}</div>}
              </div>
              {g.system_key === 'owner' && <Badge color="#c2410c">Owner</Badge>}
              {canManage && <Button size="icon" variant="ghost" title={`Remove ${g.role_name}`} aria-label={`Remove ${g.role_name}`}
                onClick={() => void ask(`Remove ${g.role_name} (${scopeLabel(g.scope, g.scope_name)}) from ${g.principal_name}?`).then(ok => { if (ok) return run(() => api.revokeRoleGrant(ws!, g.id), 'Role removed'); })}><Trash2 className="h-4 w-4" /></Button>}
            </li>
          ))}
          {!grants.data?.length && <li className="px-3 py-3 text-sm text-slate-500">No roles granted {props.mode === 'scope' ? 'here' : 'directly'}.</li>}
        </ul>
      )}
      {indirect.length > 0 && (
        <div className="text-xs text-slate-500">
          <div className="mb-1 font-medium text-slate-600 dark:text-slate-300">Also has, through departments, groups and memberships:</div>
          <ul className="flex flex-wrap gap-1.5">
            {indirect.map((g, i) => <li key={i} className="rounded-full bg-slate-100 px-2 py-0.5 dark:bg-slate-800">{g.role_name} · {scopeLabel(g.scope, g.scope_name)}{g.via === 'team' ? ` (via ${g.via_name})` : ''}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}
